import { useEffect, useState, memo, forwardRef } from 'react';
/** @jsxRuntime classic */

/** @jsx jsx */

import { jsx } from '@emotion/core';
import ReactDatepicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { getI18n } from 'react-i18next';
import moment from 'moment';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { CalendarIcon, CloseIcon } from '../../../icon';
import 'react-datepicker/dist/react-datepicker.css';
import style, { parentDiv } from './datePicker.style';
import { mediaQuery } from '../../../utils';
/**
 * This component represent a custom input used to select dates.
 * @param {string} value The date.
 * @param {function} onClick Function that will be triggered every time a user clicks the input.
 * @param {string} placeholder Text that will be shown when the user has not type into the input.
 * @param {JSON} containerStyle Custom styles apply to the div that wraps the input.
 * @param {JSON} iconStyle Custom style applied to the input icon.
 * @param {boolean} disabled Boolean to know if the input is enabled or disabled.
 * @param {boolean} showClearDate Boolean to know if the input shows the clear date icon or not.
 * @param {function} clearDate Function to clear the date.
 * @param {*} rest All other supported props.
 */

const CustomInput = /*#__PURE__*/forwardRef(({
  value,
  onClick,
  placeholder,
  containerStyle,
  iconStyle,
  disabled,
  showClearDate,
  clearDate,
  ...rest
}, ref) => {
  var _containerStyle$width;

  return jsx("div", {
    css: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: (_containerStyle$width = containerStyle === null || containerStyle === void 0 ? void 0 : containerStyle.width) !== null && _containerStyle$width !== void 0 ? _containerStyle$width : '100%',
      [mediaQuery[0]]: {
        width: '100%'
      }
    }
  }, jsx("div", {
    className: `${disabled ? 'datePicker-disable' : ''}`,
    onClick: onClick,
    role: "menuitem",
    tabIndex: 0,
    style: {
      width: '100%'
    }
  }, jsx("input", {
    value: value,
    readOnly: true,
    placeholder: placeholder,
    css: { ...containerStyle,
      width: '100%'
    },
    disabled: disabled,
    ref: ref,
    id: rest.ariaLabelledby
  })), showClearDate ? jsx(CloseIcon, {
    className: "times-icon",
    css: { ...iconStyle
    },
    onClick: clearDate
  }) : jsx(CalendarIcon, {
    name: "calendar",
    className: "calendar-icon",
    css: { ...iconStyle
    },
    onClick: onClick
  }));
});
const locales = {
  es,
  en: enUS
};
/**
 * This component represent a date picker.
 * @param {string} placeholder Text that will be shown when the user has not type into the date picker.
 * @param {string} format The date for the CustomInput.
 * @param {string} resultFormat The date for the date picker.
 * @param {string} minDate Minimun date.
 * @param {string} maxDate Maximun date.
 * @param {function} onChange Function triggered every time the date changes.
 * @param {JSON} containerStyle Custom styles apply to the div that wraps the input.
 * @param {JSON} iconStyle Custom style applied to the input icon.
 * @param {string} parentWidth Div parent width, usually set to 100%.
 * @param {string} value The date.
 * @param {boolean} showMonthDropdown Boolean to decide if show the month dropdown or not, default: false.
 * @param {boolean} showYearDropdown Boolean to decide if show the year dropdown or not, default: false.
 * @param {boolean} showClearDate Boolean to know if the input shows the clear date icon or not.
 * @param {boolean} disabled Boolean to know if the picker is enabled or disabled.
 * @param {string} app Used to determine the app, default=""
 * @param {boolean} selectsStart Used when range dates are the focus, determines the started date.
 * @param {boolean} selectsEnd Used when range dates are the focus, determines the end date.
 * @param {boolean} unControlled Boolean to determine if the date picker needs to be controlled or not be a use state.
 * @param {string} id Input id.
 */

const DatePicker = /*#__PURE__*/memo(({
  placeholder = '',
  format: formatDate = 'dd-MM-yyyy',
  resultFormat = 'yyyy-MM-dd',
  minDate = '',
  maxDate = '',
  onChange,
  containerStyle,
  iconStyle,
  parentWidth = '100%',
  value = '',
  showMonthDropdown = false,
  showYearDropdown = false,
  showClearDate = false,
  disabled = false,
  app = '',
  // esto es para usar range dates
  selectsStart = false,
  selectsEnd = false,
  unControlled = false,
  id = ''
}) => {
  const [selectedDay, setSelected] = useState('');
  useEffect(() => {
    Object.keys(locales).forEach(locale => {
      registerLocale(locale, locales[locale]);
    }); // si viene de la app CrearEmpresa por default calendario sera español siempre

    if (app === 'CE') {
      setDefaultLocale('es');
    } else if (getI18n()) {
      const lng = getI18n().language.split('-')[0];
      setDefaultLocale(lng);
    } else {
      setDefaultLocale('es');
    }

    if (value) {
      const date = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('ddd D MMM YYYY HH:mm:ss');
      setSelected(new Date(date));
    }

    if ((value === null || value === void 0 ? void 0 : value.length) === 0) {
      setSelected('');
    }
  }, [value]);

  const setDate = date => {
    if (unControlled) {
      onChange(format(date, resultFormat));
    } else {
      setSelected(date);
      onChange(format(date, resultFormat));
    }
  };

  const clearDate = () => {
    setSelected('');
    onChange('');
  };

  return jsx("div", {
    css: parentDiv(parentWidth)
  }, jsx("div", {
    css: style
  }, jsx(ReactDatepicker, {
    calendarClassName: "custom-calendar",
    popperModifiers: {
      flip: {
        behavior: ['auto']
      }
    },
    selected: selectedDay,
    customInput: // eslint-disable-next-line react/jsx-wrap-multilines
    jsx(CustomInput, {
      containerStyle: containerStyle,
      iconStyle: iconStyle,
      showClearDate: showClearDate && selectedDay,
      clearDate: clearDate,
      ariaLabelledby: id,
      id: id
    }),
    onChange: date => setDate(date),
    dateFormat: formatDate,
    minDate: minDate,
    maxDate: maxDate,
    placeholderText: placeholder || '',
    peekNextMonth: true,
    showMonthDropdown: showMonthDropdown,
    showYearDropdown: showYearDropdown,
    dropdownMode: "select",
    disabled: disabled,
    selectsStart: selectsStart,
    selectsEnd: selectsEnd
  })));
});
export default DatePicker;